import FieldIcon from '@atlaskit/icon/core/field';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import { token } from '@atlaskit/tokens';
import { UpdatedEntryIcon } from '@databases/icons/UpdatedEntryIcon';
import React, { lazy, memo, Suspense } from 'react';
import { type EntryLinkReadContent } from './entry-link-read-content.ts';
import styles from './entry-link-value-view.module.css';

const HoverCard = lazy(() =>
  import('@atlaskit/smart-card/hover-card').then((module) => ({
    default: module.HoverCard
  }))
);
const actionOptions = {
  hide: false as const,
  previewAction: { hideBlanket: true, size: 'small' as const }
};

/**
 * V1 entry pills and loading skeleton; application composition owns database access.
 */
export const EntryLinkValueView = memo(function EntryLinkValueView({
  content,
  noFocus = false,
  isMobile = false
}: {
  readonly content: EntryLinkReadContent;
  readonly noFocus?: boolean;
  readonly isMobile?: boolean;
}) {
  if (content.loadingWidth !== undefined) {
    return (
      <ul className={styles.value}>
        <li>
          <span className={styles.wrapper} data-disabled={isMobile || undefined}>
            <span className={styles.icon}>
              <FieldIcon color={token('color.icon.accent.purple')} label="" />
            </span>
            <span className={styles.text}>
              <span className={styles.skeleton} style={{ width: content.loadingWidth }} />
            </span>
          </span>
        </li>
      </ul>
    );
  }

  if (!content.links.length) {
    return null;
  }

  return (
    <ul className={styles.value}>
      {content.links.map((link) => {
        if (link.status === 'broken') {
          return (
            <li key={link.key}>
              <span
                className={styles.wrapper}
                data-disabled={isMobile || undefined}
                data-testid="table-v2-entry-link-broken"
              >
                <span className={styles.icon}>
                  <StatusErrorIcon color={token('color.icon.danger')} label="" />
                </span>
                <span className={styles.text}>
                  <span className={styles.broken}>{link.label}</span>
                </span>
              </span>
            </li>
          );
        }

        const body = (
          <>
            <span className={styles.icon}>
              <UpdatedEntryIcon label="" />
            </span>
            <span className={styles.text}>{link.label}</span>
          </>
        );
        const anchor = (
          <a
            className={styles.wrapper}
            href={link.href}
            target="_blank"
            rel="noopener noreferrer"
            tabIndex={noFocus ? -1 : undefined}
            data-disabled={isMobile || undefined}
            data-testid="table-v2-entry-link-link"
          >
            {body}
          </a>
        );

        return (
          <li key={link.key}>
            <Suspense fallback={anchor}>
              <HoverCard
                url={isMobile ? '' : (link.href ?? '')}
                // @ts-expect-error Atlaskit does not export EmbedModalSize.
                actionOptions={actionOptions}
              >
                {anchor}
              </HoverCard>
            </Suspense>
          </li>
        );
      })}
    </ul>
  );
});
